import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import { OptionallyVisible } from 'components/optionallyVisible';

import { formatDateLocal } from 'utils/date';
import formatNumber from 'utils/numbers';
import { capitalizeFirstLetter } from 'utils/string';

import { KEYS, LABELS } from '../../keys';
import { OrderPwcSectionProps } from '../../types';
import { AmountBlock, AmountNumericValue, AmountValue, Item, ItemName, ItemValue, SectionWrapper } from '../styles';
import { formatCryptoCode } from '../utils';

export const RefundExchangeDetailsSection = ({ orderData, rowId, isS4cUser }: OrderPwcSectionProps) => {
  const { t } = useTranslation();

  const refund = orderData?.refunds.find((charge) => charge.uuid === rowId);

  return (
    <Panel label={t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.TITLE)}>
      <SectionWrapper>
        <OptionallyVisible visible={isS4cUser}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.LIQUIDITY_PROVIDER)}</ItemName>
            <ItemValue>{refund?.liquidityProvider ? capitalizeFirstLetter(refund?.liquidityProvider) : t(KEYS.DASH)}</ItemValue>
          </Item>

          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.LIQUIDITY_PROVIDER_ID)}</ItemName>
            <ItemValue>{refund?.liquidityProviderId ? capitalizeFirstLetter(refund?.liquidityProviderId) : t(KEYS.DASH)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.cryptoPrice)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.CRYPTO_PRICE)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountNumericValue>{KEYS.BASE_CURRENCY_UNIT}</AmountNumericValue>
                <AmountValue>{formatCryptoCode(refund?.cryptoCurrencyCode)}</AmountValue>
                <AmountValue>{KEYS.APPROX_EQUAL_SYMBOL}</AmountValue>
                <AmountNumericValue>{formatNumber(Number(refund?.cryptoPrice))}</AmountNumericValue>
                <AmountValue>{refund?.fiatCurrencyCode}</AmountValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.settlementAmount && refund?.settlementCurrencyCode)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.SETTLEMENT_AMOUNT)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountNumericValue>{formatNumber(Number(refund?.settlementAmount))}</AmountNumericValue>
                <AmountValue>{formatCryptoCode(refund?.settlementCurrencyCode)}</AmountValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.fxRate)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.FX_RATE)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountNumericValue>{KEYS.BASE_CURRENCY_UNIT}</AmountNumericValue>
                <AmountValue>{formatCryptoCode(refund?.settlementCurrencyCode)}</AmountValue>
                <AmountValue>{KEYS.APPROX_EQUAL_SYMBOL}</AmountValue>
                <AmountNumericValue>{refund?.fxRate}</AmountNumericValue>
                <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={isS4cUser && Boolean(refund?.fxRateSource)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.FX_RATE_SOURCE)}</ItemName>
            <ItemValue>{refund?.fxRateSource}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.fxRateCapturedAt)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.FX_RATE_CAPTURED_AT)}</ItemName>
            <ItemValue>{formatDateLocal(refund?.fxRateCapturedAt)}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.networkFee)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_EXCHANGE_DETAILS.NETWORK_FEE)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountNumericValue>{refund?.networkFee ? formatNumber(Number(refund?.networkFee)) : t(KEYS.DASH)}</AmountNumericValue>
                <AmountValue>{formatCryptoCode(refund?.cryptoCurrencyCode ?? refund?.settlementCurrencyCode)}</AmountValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
